import React, { useContext } from 'react';
import { VectorLayer } from '@meitu/saas-editor-core/es/layers/vector-layer';
import { TextWritingType, type IVectorLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { FontStyleGroup, type Features } from '@/components/font-style-group';
import { useEditor } from '@/hooks/use-editor';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { VectorPanelContext } from '../../context';
import { Tracker } from '../../utils';

export const FontStyle: React.FC = () => {
    const { layerId, recordId } = useContext(VectorPanelContext)!;

    const editor = useEditor();
    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return null;

        const { bold, italic, lineThrough, underline, writingMode } = textInfo;

        return {
            bold,
            italic,
            lineThrough,
            underline,
            writingMode,
        };
    });

    const bold = props ? props.bold : false;
    const italic = props ? props.italic : false;
    const lineThrough = props ? props.lineThrough : false;
    const underline = props ? props.underline : false;
    const writingMode = props ? props.writingMode : TextWritingType.HORIZONTAL_TB;

    const handleChange = (kind: Features, value: boolean | string) => {
        if (!props) return;

        switch (kind) {
            case 'bold':
                editor.getInstance(VectorLayer).setBoldStyle(layerId, value as boolean);
                break;
            case 'italic':
                editor.getInstance(VectorLayer).setItalicStyle(layerId, value as boolean);
                break;
            case 'lineThrough':
                editor.getInstance(VectorLayer).setLineThrough(layerId, value as boolean);
                break;
            case 'underline':
                editor.getInstance(VectorLayer).setUnderline(layerId, value as boolean);
                break;
            case 'writingMode':
                editor.getInstance(VectorLayer).setWritingMode(layerId, value as TextWritingType);
                break;
        }

        Tracker.setFontStyle({
            recordId,
            stageType: editor.stage.getType(),
            type: kind,
            value,
            isVectorLayer: true,
        });
    };

    return (
        <FontStyleGroup
            bold={bold}
            italic={italic}
            lineThrough={lineThrough}
            underline={underline}
            writingMode={writingMode}
            onChange={handleChange}
        />
    );
};
